import * as React from 'react';
import {Component} from 'react';
import {observer} from 'mobx-react';
import {observable, makeObservable} from 'mobx';
import {
    PriorityIcon,
    Icon,
    Flex,
    flexColumnProps,
    Button,
    Input,
    Dropdown
} from '@priority-software/priority-style-react';
import {getLocalizedText, texts} from '../../../constants/TextConst';
import {saveCustomSearch, getSearchSavedMessage, executeAdvancedSearch} from '../actions/filters';
import {setDefaultSearch, validateAndSetName} from '../actions/searchesList';
import {showConfirmMessage} from '../../../_shared/Messages/actions/MessageActions';
import {setNewSearch} from '../../../_shared/GWTBridge/invoke/formTable';
import {SEARCH_NAME_MAX_LENGTH} from '../consts/searches';
import {MultiButton} from './MultiButton';
import messageTexts from '../../../_shared/Messages/texts';
import {showSuccessToast} from '../../../components/Toast/actions/ToastActions';
import {Label} from '../../../components/Common/Label';
import {formsStore} from '../../Form/stores/FormsStore';

@observer
export class SearchesFooter extends Component<any, any> {
    @observable saveMode;
    @observable searchTitle;
    constructor(props) {
        super(props);
        makeObservable(this);
        this.saveMode = false;
        this.searchTitle = '';
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.selected !== this.props.selected) {
            this.saveMode = false;
            this.searchTitle = '';
        }
    }

    toggleSaveMode = () => {
        const {selected} = this.props;
        if (selected.isPrevious) {
            this.searchTitle = selected.name;
        }
        this.saveMode = !this.saveMode;
    };

    onTitleChange = event => {
        if (event.target.value.length <= SEARCH_NAME_MAX_LENGTH) {
            this.searchTitle = event.target.value;
        }
    };

    onApply = (focus = true) => {
        const {selected, closePanel, onApply} = this.props;
        executeAdvancedSearch(selected);
        closePanel(focus);
        onApply?.();
    };

    onAddToList = (focus = true) => {
        const {selected, closePanel} = this.props;
        executeAdvancedSearch(selected, true);
        closePanel(focus);
    };

    handleSaveSearch = isNew => {
        const {searchesStore} = formsStore;
        const {defaultIdx} = searchesStore;
        const formHasDefaultSearch = defaultIdx > 0;
        const {selected} = this.props;
        const saveFunc = isNew ? this.onSaveAndApplyNewSearch : this.onSaveAndApply;
        const searchTitle = isNew ? this.searchTitle : selected.name;
        if (!formHasDefaultSearch) {
            showConfirmMessage({
                title: messageTexts.setAsDefualt.replace('XXX', searchTitle),
                confirmButtonText: messageTexts.yes,
                cancleButtonText: messageTexts.no,
                onConfirm: () => saveFunc(true),
                onCancel: () => saveFunc(false),
                defaultButton: 0
            });
        } else {
            saveFunc(false);
        }
    };

    onSaveAndApply = isDefault => {
        const {selected} = this.props;
        !selected.isNewNameSaved && validateAndSetName(selected);
        saveCustomSearch(selected).then(res => {
            this.onApply();
            isDefault && setDefaultSearch(selected);
        });
    };

    onSaveAndApplyNewSearch = isDefault => {
        const {selected} = this.props;
        selected.setNewName(this.searchTitle);
        selected.setName(this.searchTitle);
        saveCustomSearch(selected).then(res => {
            //adds new search to form queries map (in gwt)
            //so that the a new shortkey would be reserved for the search
            setNewSearch(selected.index, selected.name);
            isDefault && setDefaultSearch(selected);
            this.onApply(false);
            showSuccessToast(getSearchSavedMessage(selected));
        });
    };

    onClear = () => {
        const {selected, onClear} = this.props;
        selected.clearFilters();
        onClear?.();
    };

    saveAsNew = () => {};

    render() {
        const {selected, searchBtnRef} = this.props;
        const {canSave, canExecute} = selected;
        return (
            <Flex {...flexColumnProps} componentClasses="footer">
                {this.saveMode ? (
                    <React.Fragment>
                        <Label skin="paragraph-2" textColor="white">
                            {getLocalizedText(texts.saveAndNameSearch)}
                        </Label>

                        <Flex paddingTop componentClasses="save-row">
                            <Input
                                size="large"
                                value={this.searchTitle}
                                autoFocus={true}
                                onChange={this.onTitleChange}
                                placeholder={getLocalizedText(texts.nameSearch)}
                                componentClasses="search-name-input"
                            />

                            <Button
                                width="regularFixed"
                                size="medium"
                                onClick={() => this.handleSaveSearch(true)}
                                disabled={!this.searchTitle.length}
                            >
                                {getLocalizedText(texts.saveAndApply)}
                            </Button>

                            <Icon
                                icon={PriorityIcon.closeSmall}
                                skin="standart"
                                onClick={this.toggleSaveMode}
                            />
                        </Flex>
                    </React.Fragment>
                ) : (
                    <Flex componentClasses="footer-row">
                        {canExecute && (
                            <Button
                                componentClasses="btn-clear-search"
                                skin="text"
                                prefixIcon={PriorityIcon.clear}
                                onClick={this.onClear}
                            >
                                {getLocalizedText(texts.clearSearch)}
                            </Button>
                        )}

                        <Flex componentClasses="buttons-container">
                            {selected.isNew || selected.isPrevious ? (
                                <React.Fragment>
                                    <Button
                                        skin="secondary"
                                        size="medium"
                                        width="regularFixed"
                                        disabled={!canSave}
                                        onClick={this.toggleSaveMode}
                                        componentClasses="searches-footer-save-btn"
                                    >
                                        {getLocalizedText(texts.save)}
                                    </Button>
                                    <MultiButton
                                        dropdownClassName="searches-footer-dropdown"
                                        button={
                                            <Button
                                                size="medium"
                                                width="regularFixed"
                                                disabled={!canExecute}
                                                onClick={this.onApply}
                                                inputRef={searchBtnRef}
                                                componentClasses="searches-footer-apply-btn"
                                            >
                                                {getLocalizedText(texts.apply)}
                                            </Button>
                                        }
                                        triggerProps={{
                                            size: 'medium',
                                            disabled: !canExecute,
                                            componentClasses: 'searches-footer-trigger-btn'
                                        }}
                                    >
                                        <Dropdown.Item index={0} onClick={this.onApply}>
                                            <Label
                                                skin="paragraph-2"
                                                textColor="primary-700"
                                                wrap={false}
                                            >
                                                {getLocalizedText(texts.apply)}
                                            </Label>
                                        </Dropdown.Item>

                                        <Dropdown.Item index={1} onClick={this.onAddToList}>
                                            <Label
                                                skin="paragraph-2"
                                                textColor="primary-700"
                                                wrap={false}
                                            >
                                                {getLocalizedText(texts.applyAddToList)}
                                            </Label>
                                        </Dropdown.Item>
                                    </MultiButton>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <MultiButton
                                        dropdownClassName="searches-footer-dropdown"
                                        button={
                                            <Button
                                                size="medium"
                                                width="regularFixed"
                                                disabled={!canSave}
                                                onClick={() => this.handleSaveSearch(false)}
                                                componentClasses="ignore-react-onclickoutside footer-save-search-btn"
                                            >
                                                {getLocalizedText(texts.saveAndApply)}
                                            </Button>
                                        }
                                        triggerProps={{
                                            size: 'medium',
                                            disabled: !canSave,
                                            componentClasses: 'ignore-react-onclickoutside'
                                        }}
                                    >
                                        <Dropdown.Item
                                            index={0}
                                            divider={true}
                                            onClick={() => this.handleSaveSearch(false)}
                                        >
                                            <Label
                                                skin="paragraph-2"
                                                textColor="primary-700"
                                                wrap={false}
                                            >
                                                {getLocalizedText(texts.saveAndApply)}
                                            </Label>
                                        </Dropdown.Item>

                                        <Dropdown.Item index={1} onClick={this.onApply}>
                                            <Label
                                                skin="paragraph-2"
                                                textColor="primary-700"
                                                wrap={false}
                                            >
                                                {getLocalizedText(texts.apply)}
                                            </Label>
                                        </Dropdown.Item>

                                        <Dropdown.Item index={2} onClick={this.onAddToList}>
                                            <Label
                                                skin="paragraph-2"
                                                textColor="primary-700"
                                                wrap={false}
                                            >
                                                {getLocalizedText(texts.applyAddToList)}
                                            </Label>
                                        </Dropdown.Item>
                                    </MultiButton>
                                </React.Fragment>
                            )}
                        </Flex>
                    </Flex>
                )}
            </Flex>
        );
    }
}
